// Fetch-based SSE streaming for POST /v1/assistant/chat.
//
// We deliberately do NOT use EventSource: it is GET-only and can't send a
// request body, and we need to POST {message, media_ids} carrying the session
// cookie + X-Org-Id header. Instead we POST and read the ReadableStream body,
// decoding chunks into the incremental SSE parser.
//
// Reconnect-safety: this function never hangs. It throws a StreamError on any
// transport failure (network drop, non-2xx, aborted, or a stream that ends
// without event:done). The caller (useAssistant) surfaces a retry affordance
// instead of a frozen spinner.

import { API_BASE } from '../api/client.ts';
import { getActiveOrgId } from '../api/org-store.ts';
import { AssistantSseParser } from './sse.ts';
import type { AssistantEvent } from './types.ts';

export interface ChatRequest {
  conversation_id?: string;
  message: string;
  media_ids?: string[];
}

export class StreamError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'StreamError';
    this.code = code;
  }
}

export interface StreamCallbacks {
  onEvent: (ev: AssistantEvent) => void;
  signal?: AbortSignal;
}

/**
 * Open the chat stream and pump events to `onEvent` until the server closes it.
 * Resolves when the stream ends cleanly, rejects (StreamError) on transport
 * failure. Whether a `done` event actually arrived is the caller's concern —
 * useAssistant treats "stream closed with no done" as a recoverable drop.
 */
export async function streamChat(body: ChatRequest, cb: StreamCallbacks): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const orgId = getActiveOrgId();
  if (orgId) headers['X-Org-Id'] = orgId;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/assistant/chat`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify(body),
      signal: cb.signal,
    });
  } catch (err) {
    if (cb.signal?.aborted) throw new StreamError('aborted', 'Stream aborted');
    throw new StreamError('network', err instanceof Error ? err.message : 'Network error');
  }

  if (!res.ok) {
    // Try to surface the JSON error envelope if the server sent one.
    let message = res.statusText || 'Request failed';
    let code = `http_${res.status}`;
    try {
      const data = (await res.json()) as { error?: { code?: string; message?: string } };
      if (data.error?.message) message = data.error.message;
      if (data.error?.code) code = data.error.code;
    } catch {
      /* non-JSON error body */
    }
    throw new StreamError(code, message);
  }

  if (!res.body) throw new StreamError('no_body', 'Stream had no body');

  const parser = new AssistantSseParser();
  const reader = res.body.getReader();
  const decoder = new TextDecoder();

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      // `stream: true` keeps multi-byte UTF-8 sequences intact across chunks.
      const text = decoder.decode(value, { stream: true });
      if (text) for (const ev of parser.push(text)) cb.onEvent(ev);
    }
    const tail = decoder.decode();
    if (tail) for (const ev of parser.push(tail)) cb.onEvent(ev);
    for (const ev of parser.flush()) cb.onEvent(ev);
  } catch (err) {
    if (cb.signal?.aborted) throw new StreamError('aborted', 'Stream aborted');
    throw new StreamError('stream', err instanceof Error ? err.message : 'Stream read failed');
  } finally {
    reader.releaseLock();
  }
}
